import React, { Fragment, useCallback, useMemo } from 'react';
import { Modal } from 'antd';
import { Column, CopyableDataSection, Row, Text } from '@ydistri/ds';
import { styled } from 'styled-components';
import type {
  MultiselectCustomerPartialEntity,
  MultiselectKeysToParse,
  MultiselectParseResponse,
} from './configurationMultiselectTypes';
import {
  buildAdditiveMultiselectSelection,
  buildMultiselectValidationSections,
} from './configurationMultiselectModalLib';

const titles = {
  customerId: 'ID',
  code: 'Code',
};

interface MultiselectValidationModalProps {
  parsedData?: MultiselectParseResponse;
  selectedIds?: number[];
  data?: MultiselectCustomerPartialEntity[];
  dataCheck?: MultiselectCustomerPartialEntity[];
  dataCheckLabel?: string;
  compareAsInt?: boolean;
  onClose?: () => void;
  onSubmit?: (data: number[]) => void;
}

const SectionsGrid = styled.div<{ $sectionCount: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$sectionCount}, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const MultiselectValidationModal: React.FC<MultiselectValidationModalProps> = ({
  parsedData,
  selectedIds = [],
  data = [],
  dataCheck,
  dataCheckLabel,
  compareAsInt = false,
  onClose,
  onSubmit,
}) => {
  const onCloseHandler = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const onSubmitHandler = useCallback(() => {
    if (parsedData && onSubmit) {
      onSubmit(buildAdditiveMultiselectSelection(selectedIds, parsedData.ids));
    }
  }, [parsedData, onSubmit, selectedIds]);

  const availableTypes: MultiselectKeysToParse[] = useMemo(() => {
    if (parsedData) {
      return (
        Object.entries(parsedData.availability)
          .filter(entry => entry[1])
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the keys are of the correct type
          .map(entry => entry[0] as MultiselectKeysToParse)
      );
    }
    return [];
  }, [parsedData]);

  const sections = useMemo(() => {
    if (!parsedData) {
      return;
    }

    return buildMultiselectValidationSections({
      parsedData,
      selectedIds,
      data,
      dataCheck,
      compareAsInt,
    });
  }, [compareAsInt, data, dataCheck, parsedData, selectedIds]);

  if (!parsedData || !sections) {
    // eslint-disable-next-line no-console -- we want the output here
    console.log('We dont have parsed data!');
    return null;
  }

  const hasUnrecognizedData = parsedData.notRecognized.length > 0;
  const hasDataCheckOnly = (sections.dataCheckOnly?.data.length ?? 0) > 0;

  return (
    <Modal
      open={true}
      title="Pasted data inconsistency"
      onCancel={onCloseHandler}
      okText="Add identified"
      cancelText="Cancel"
      onOk={onSubmitHandler}
      width={900}
    >
      <Column $gap="1rem">
        <Text>
          {hasUnrecognizedData
            ? "We couldn't recognize some of the pasted data"
            : `Some pasted data is ${dataCheckLabel?.toLowerCase() ?? 'outside of scope'}`}
          {availableTypes.length > 1 && (
            <Text>
              {' '}
              (we assumed <Text $bold>{titles[parsedData.parsedBy]}</Text> was pasted)
            </Text>
          )}
        </Text>
        <Row $gap="1rem">
          {availableTypes.map(type => {
            return (
              <Fragment key={type}>
                <Text $bold>{titles[type]}:</Text>
                <Text>
                  {parsedData.counter[type]} of {parsedData.counter.total} identified{' '}
                </Text>
              </Fragment>
            );
          })}
        </Row>

        <SectionsGrid $sectionCount={hasDataCheckOnly ? 4 : 3}>
          <CopyableDataSection
            title="To keep"
            data={sections.kept.data}
            dataToDisplay={sections.kept.dataToDisplay}
            emptyText="Nothing will stay selected"
            variant="neutral"
          />
          <CopyableDataSection
            title="To add"
            data={sections.added.data}
            dataToDisplay={sections.added.dataToDisplay}
            emptyText="Nothing will be added"
            variant="success"
          />
          {hasDataCheckOnly && sections.dataCheckOnly && (
            <CopyableDataSection
              title={dataCheckLabel ?? 'Outside of scope'}
              data={sections.dataCheckOnly.data}
              dataToDisplay={sections.dataCheckOnly.dataToDisplay}
              emptyText={`Nothing is ${dataCheckLabel?.toLowerCase() ?? 'outside of scope'}`}
              variant="danger"
            />
          )}
          <CopyableDataSection
            title="Not identified"
            data={sections.notRecognized.data}
            emptyText="Everything was identified"
            variant="danger"
          />
        </SectionsGrid>
      </Column>
    </Modal>
  );
};

export default MultiselectValidationModal;
